import clsx from 'clsx';
import {
  type CSSProperties,
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type PointerEvent,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import * as styles from './index.css';

const ACTION_REVEAL_EVENT = 'streamlain:sidebar-actions-active';
export const SIDEBAR_ACTION_REVEAL_CLOSE_EVENT =
  'streamlain:sidebar-actions-close';
export const SIDEBAR_ACTION_REVEAL_LOCK_EVENT =
  'streamlain:sidebar-actions-lock';
export const SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR =
  'data-sidebar-action-reveal-keep-active';
const ACTION_REVEAL_DISTANCE = 48;
const ACTION_REVEAL_TRANSITION_MS = 180;
const ACTION_REVEAL_DRAG_THRESHOLD = 6;

const clampActionProgress = (value: number) => Math.max(0, Math.min(1, value));

type SidebarActionRevealStyle = CSSProperties & {
  '--streamlain-action-reveal-actions-width'?: string;
  '--streamlain-action-reveal-offset'?: string;
  '--streamlain-action-reveal-opacity'?: number;
};

type SidebarActionRevealProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    actions?: ReactNode;
    actionsClassName?: string;
    contentClassName?: string;
  }
>;

export const SidebarActionReveal = forwardRef<
  HTMLDivElement,
  SidebarActionRevealProps
>(
  (
    {
      actions,
      actionsClassName,
      children,
      className,
      contentClassName,
      onBlur,
      onClick,
      onFocus,
      onPointerCancel,
      onPointerDown,
      onPointerEnter,
      onPointerLeave,
      onPointerMove,
      onPointerUp,
      style,
      ...attrs
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const actionId = useId();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const actionsRef = useRef<HTMLDivElement | null>(null);
    const activeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragRef = useRef<{
      pointerId: number;
      startX: number;
      startY: number;
      startProgress: number;
      progress: number;
      scrubbing: boolean;
    } | null>(null);
    const suppressClickRef = useRef(false);
    const [actionProgress, setActionProgress] = useState(0);
    const [actionRevealed, setActionRevealed] = useState(false);
    const [actionScrubbing, setActionScrubbing] = useState(false);
    const [actionsLocked, setActionsLocked] = useState(false);
    const [mouseActionActive, setMouseActionActive] = useState(false);
    const hasActions =
      actions !== undefined && actions !== null && actions !== false;

    const setRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        rootRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    useEffect(() => {
      const root = rootRef.current;
      const actionRail = actionsRef.current;
      if (!root || !actionRail || typeof ResizeObserver === 'undefined') {
        return;
      }

      const updateActionsWidth = () => {
        root.style.setProperty(
          '--streamlain-action-reveal-actions-width',
          `${actionRail.getBoundingClientRect().width}px`
        );
      };
      const resizeObserver = new ResizeObserver(updateActionsWidth);
      resizeObserver.observe(actionRail);
      updateActionsWidth();

      return () => resizeObserver.disconnect();
    }, [hasActions]);

    const dispatchActionsActive = useCallback(
      (active: boolean, delay = 0) => {
        if (!hasActions) {
          return;
        }
        if (activeTimerRef.current) {
          clearTimeout(activeTimerRef.current);
          activeTimerRef.current = null;
        }

        const dispatch = () => {
          rootRef.current?.dispatchEvent(
            new CustomEvent(ACTION_REVEAL_EVENT, {
              bubbles: true,
              detail: { active, id: actionId },
            })
          );
        };

        if (delay > 0) {
          activeTimerRef.current = setTimeout(dispatch, delay);
          return;
        }

        dispatch();
      },
      [actionId, hasActions]
    );

    useEffect(() => {
      return () => {
        if (activeTimerRef.current) {
          clearTimeout(activeTimerRef.current);
        }
        rootRef.current?.dispatchEvent(
          new CustomEvent(ACTION_REVEAL_EVENT, {
            bubbles: true,
            detail: { active: false, id: actionId },
          })
        );
      };
    }, [actionId]);

    const closeActionsAfterTransition = useCallback(() => {
      dispatchActionsActive(false, ACTION_REVEAL_TRANSITION_MS);
    }, [dispatchActionsActive]);

    const closeActions = useCallback(() => {
      dragRef.current = null;
      setActionScrubbing(false);
      setActionRevealed(false);
      setActionProgress(0);
      setMouseActionActive(false);
      if (!actionsLocked) {
        closeActionsAfterTransition();
      }
    }, [actionsLocked, closeActionsAfterTransition]);

    const forceCloseActions = useCallback(() => {
      if (activeTimerRef.current) {
        clearTimeout(activeTimerRef.current);
        activeTimerRef.current = null;
      }
      dragRef.current = null;
      setActionScrubbing(false);
      setActionRevealed(false);
      setActionProgress(0);
      setMouseActionActive(false);
      setActionsLocked(false);
      dispatchActionsActive(false);
    }, [dispatchActionsActive]);

    const handleActionsLockChange = useCallback(
      (event: Event) => {
        const detail = (event as CustomEvent<{ active?: boolean }>).detail;
        const nextActionsLocked = Boolean(detail?.active);
        setActionsLocked(nextActionsLocked);
        if (nextActionsLocked) {
          dispatchActionsActive(true);
          return;
        }
        if (!mouseActionActive && !actionRevealed && !actionScrubbing) {
          closeActionsAfterTransition();
        }
      },
      [
        actionRevealed,
        actionScrubbing,
        closeActionsAfterTransition,
        dispatchActionsActive,
        mouseActionActive,
      ]
    );

    useEffect(() => {
      const root = rootRef.current;
      if (!root) {
        return;
      }

      root.addEventListener(
        SIDEBAR_ACTION_REVEAL_LOCK_EVENT,
        handleActionsLockChange
      );
      root.addEventListener(
        SIDEBAR_ACTION_REVEAL_CLOSE_EVENT,
        forceCloseActions
      );
      return () => {
        root.removeEventListener(
          SIDEBAR_ACTION_REVEAL_LOCK_EVENT,
          handleActionsLockChange
        );
        root.removeEventListener(
          SIDEBAR_ACTION_REVEAL_CLOSE_EVENT,
          forceCloseActions
        );
      };
    }, [forceCloseActions, handleActionsLockChange]);

    const updateMouseActionState = useCallback(() => {
      if (!hasActions) {
        if (mouseActionActive) {
          closeActions();
        }
        return;
      }

      if (!mouseActionActive) {
        setMouseActionActive(true);
        dispatchActionsActive(true);
      }
    }, [closeActions, dispatchActionsActive, hasActions, mouseActionActive]);

    const handleClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (
          event.target instanceof Node &&
          actionsRef.current?.contains(event.target)
        ) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (suppressClickRef.current) {
          event.preventDefault();
          event.stopPropagation();
          suppressClickRef.current = false;
          return;
        }
        onClick?.(event);
      },
      [onClick]
    );

    const handlePointerEnter = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        onPointerEnter?.(event);
        if (event.defaultPrevented) return;
        if (event.pointerType === 'mouse') {
          updateMouseActionState();
        }
      },
      [onPointerEnter, updateMouseActionState]
    );

    const handlePointerLeave = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        onPointerLeave?.(event);
        if (event.defaultPrevented) return;
        if (event.pointerType === 'mouse') {
          closeActions();
        }
      },
      [closeActions, onPointerLeave]
    );

    useEffect(() => {
      if (!mouseActionActive) {
        return;
      }

      const handleWindowPointerMove = (event: globalThis.PointerEvent) => {
        if (event.pointerType !== 'mouse') {
          return;
        }

        const root = rootRef.current;
        if (!root) {
          closeActions();
          return;
        }

        const bounds = root.getBoundingClientRect();
        const pointerIsInside =
          event.clientX >= bounds.left &&
          event.clientX <= bounds.right &&
          event.clientY >= bounds.top &&
          event.clientY <= bounds.bottom;

        if (!pointerIsInside) {
          closeActions();
        }
      };
      const handleWindowBlur = () => closeActions();

      window.addEventListener('pointermove', handleWindowPointerMove, true);
      window.addEventListener('blur', handleWindowBlur);
      return () => {
        window.removeEventListener(
          'pointermove',
          handleWindowPointerMove,
          true
        );
        window.removeEventListener('blur', handleWindowBlur);
      };
    }, [closeActions, mouseActionActive]);

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLDivElement>) => {
        onFocus?.(event);
        if (!event.defaultPrevented) {
          dispatchActionsActive(true);
        }
      },
      [dispatchActionsActive, onFocus]
    );

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLDivElement>) => {
        onBlur?.(event);
        if (
          event.defaultPrevented ||
          (event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget))
        ) {
          return;
        }
        closeActions();
      },
      [closeActions, onBlur]
    );

    const handleActionsClickCapture = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (
          event.target instanceof Element &&
          event.target.closest(`[${SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR}]`)
        ) {
          return;
        }
        closeActions();
      },
      [closeActions]
    );

    const handlePointerDown = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        onPointerDown?.(event);
        if (
          event.defaultPrevented ||
          !hasActions ||
          event.pointerType === 'mouse'
        ) {
          return;
        }
        dragRef.current = {
          pointerId: event.pointerId,
          progress: actionRevealed ? 1 : 0,
          scrubbing: false,
          startProgress: actionRevealed ? 1 : 0,
          startX: event.clientX,
          startY: event.clientY,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
      },
      [actionRevealed, hasActions, onPointerDown]
    );

    const handlePointerMove = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        onPointerMove?.(event);
        if (event.defaultPrevented) return;
        if (event.pointerType === 'mouse') {
          updateMouseActionState();
          return;
        }

        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) {
          return;
        }

        const deltaX = drag.startX - event.clientX;
        const deltaY = event.clientY - drag.startY;
        if (!drag.scrubbing) {
          const horizontal = Math.abs(deltaX);
          if (
            horizontal < ACTION_REVEAL_DRAG_THRESHOLD ||
            horizontal < Math.abs(deltaY)
          ) {
            return;
          }
          drag.scrubbing = true;
          setActionScrubbing(true);
          setActionRevealed(true);
          dispatchActionsActive(true);
        }

        const nextProgress = clampActionProgress(
          drag.startProgress + deltaX / ACTION_REVEAL_DISTANCE
        );
        drag.progress = nextProgress;
        setActionProgress(nextProgress);
        if (event.cancelable) {
          event.preventDefault();
        }
      },
      [dispatchActionsActive, onPointerMove, updateMouseActionState]
    );

    const finishPointerGesture = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) {
          return;
        }
        dragRef.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);

        if (!drag.scrubbing) {
          return;
        }

        const nextRevealed = drag.progress >= 0.5;
        setActionScrubbing(false);
        setActionRevealed(nextRevealed);
        setActionProgress(nextRevealed ? 1 : 0);
        suppressClickRef.current = true;
        if (!nextRevealed) {
          closeActionsAfterTransition();
        }
      },
      [closeActionsAfterTransition]
    );

    const handlePointerUp = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        onPointerUp?.(event);
        if (!event.defaultPrevented) {
          finishPointerGesture(event);
        }
      },
      [finishPointerGesture, onPointerUp]
    );

    const handlePointerCancel = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        onPointerCancel?.(event);
        if (!event.defaultPrevented) {
          finishPointerGesture(event);
        }
      },
      [finishPointerGesture, onPointerCancel]
    );

    const actionStyle =
      actionScrubbing || actionRevealed
        ? ({
            '--streamlain-action-reveal-offset': `${16 * (1 - actionProgress)}px`,
            '--streamlain-action-reveal-opacity': actionProgress,
          } satisfies SidebarActionRevealStyle)
        : undefined;

    return (
      <div
        {...attrs}
        className={clsx(styles.root, className)}
        ref={setRootRef}
        onBlur={handleBlur}
        onClick={handleClick}
        onFocus={handleFocus}
        onPointerCancel={handlePointerCancel}
        onPointerDown={handlePointerDown}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        data-mouse-action-active={mouseActionActive}
        data-actions-locked={actionsLocked}
        data-action-revealed={actionRevealed}
        data-action-scrubbing={actionScrubbing}
        data-has-actions={hasActions}
        style={actionStyle ? { ...style, ...actionStyle } : style}
      >
        <div
          className={clsx(styles.content, contentClassName)}
          data-sidebar-action-reveal-content
        >
          {children}
        </div>
        {hasActions ? (
          <div
            ref={actionsRef}
            className={clsx(styles.actions, actionsClassName)}
            data-sidebar-action-reveal-actions
            onClick={event => event.stopPropagation()}
            onClickCapture={handleActionsClickCapture}
          >
            {actions}
          </div>
        ) : null}
      </div>
    );
  }
);

SidebarActionReveal.displayName = 'SidebarActionReveal';
